import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import { endpointLink } from '../index';

// Types for notification preferences
export interface NotificationPreferences {
  emailSubscribed: boolean;
  emailConfirmed: boolean;
  emailAddress?: string;
  emailVerificationToken?: string;
  smsSubscribed: boolean;
  smsConfirmed: boolean;
  phoneNumber?: string;
  smsVerificationToken?: string;
}

export interface NotificationResponse {
  success: boolean;
  preferences: NotificationPreferences;
  message?: string;
}

// Custom hook to get notification preferences
export const useNotificationPreferences = () => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const getToken = async (): Promise<string> => {
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: "https://odp.golf/audience",
          scope: "openid profile email offline_access",
        },
      });
      return token;
    } catch (error: any) {
      console.error('Token error:', error);
      if (String(error?.message || "").includes('Missing Refresh Token')) {
        console.log('Missing refresh token, redirecting to login...');
        await loginWithRedirect({
          authorizationParams: {
            audience: "https://odp.golf/audience",
            scope: "openid profile email offline_access",
            prompt: 'login'
          }
        });
      }
      throw error;
    }
  };

  return useQuery({
    queryKey: ['notificationPreferences'],
    queryFn: async (): Promise<NotificationPreferences> => {
      const token = await getToken();

      const response = await axios.get<NotificationResponse>(`${endpointLink}user/notifications`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data.preferences;
    },
    staleTime: 0, // Always consider data stale so invalidations trigger refetches
    refetchOnWindowFocus: true, // Refetch when user returns to tab
  });
};

// Custom hook to update notification preferences
export const useUpdateNotificationPreferences = () => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const queryClient = useQueryClient();

  const getToken = async (): Promise<string> => {
    try {
      return await getAccessTokenSilently({
        authorizationParams: {
          audience: "https://odp.golf/audience",
          scope: "openid profile email offline_access",
        },
      });
    } catch (error: any) {
      if (String(error?.message || "").includes('Missing Refresh Token')) {
        await loginWithRedirect({
          authorizationParams: {
            audience: "https://odp.golf/audience",
            scope: "openid profile email offline_access",
            prompt: 'login'
          }
        });
      }
      throw error;
    }
  };

  return useMutation({
    mutationFn: async (preferences: Partial<NotificationPreferences>): Promise<NotificationResponse> => {
      const token = await getToken();

      const response = await axios.post<NotificationResponse>(`${endpointLink}user/notifications`, preferences, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data;
    },
    onSuccess: () => {
      // Invalidate and refetch notification preferences
      queryClient.invalidateQueries({ queryKey: ['notificationPreferences'] });
    },
  });
};

// Custom hook to confirm email subscription
export const useConfirmEmailSubscription = () => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const queryClient = useQueryClient();

  const getToken = async (): Promise<string> => {
    try {
      return await getAccessTokenSilently({
        authorizationParams: {
          audience: "https://odp.golf/audience",
          scope: "openid profile email offline_access",
        },
      });
    } catch (error: any) {
      if (String(error?.message || "").includes('Missing Refresh Token')) {
        await loginWithRedirect({
          authorizationParams: {
            audience: "https://odp.golf/audience",
            scope: "openid profile email offline_access",
            prompt: 'login'
          }
        });
      }
      throw error;
    }
  };

  return useMutation({
    mutationFn: async (): Promise<NotificationResponse> => {
      const token = await getToken();

      const response = await axios.post<NotificationResponse>(`${endpointLink}user/notifications/confirm-email`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notificationPreferences'] });
    },
  });
};

// Custom hook to confirm SMS subscription
export const useConfirmSMSSubscription = () => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const queryClient = useQueryClient();

  const getToken = async (): Promise<string> => {
    try {
      return await getAccessTokenSilently({
        authorizationParams: {
          audience: "https://odp.golf/audience",
          scope: "openid profile email offline_access",
        },
      });
    } catch (error: any) {
      if (String(error?.message || "").includes('Missing Refresh Token')) {
        await loginWithRedirect({
          authorizationParams: {
            audience: "https://odp.golf/audience",
            scope: "openid profile email offline_access",
            prompt: 'login'
          }
        });
      }
      throw error;
    }
  };

  return useMutation({
    mutationFn: async (phoneNumber?: string): Promise<NotificationResponse> => {
      const token = await getToken();

      const response = await axios.post<NotificationResponse>(`${endpointLink}user/notifications/confirm-sms`, 
        phoneNumber ? { phoneNumber } : {}, 
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notificationPreferences'] });
    },
  });
};

// Custom hook to unsubscribe from email notifications
export const useUnsubscribeEmail = () => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const queryClient = useQueryClient();

  const getToken = async (): Promise<string> => {
    try {
      return await getAccessTokenSilently({
        authorizationParams: {
          audience: "https://odp.golf/audience",
          scope: "openid profile email offline_access",
        },
      });
    } catch (error: any) {
      if (String(error?.message || "").includes('Missing Refresh Token')) {
        await loginWithRedirect({
          authorizationParams: {
            audience: "https://odp.golf/audience",
            scope: "openid profile email offline_access",
            prompt: 'login'
          }
        });
      }
      throw error;
    }
  };

  return useMutation({
    mutationFn: async (): Promise<NotificationResponse> => {
      const token = await getToken();

      const response = await axios.post<NotificationResponse>(`${endpointLink}user/notifications/unsubscribe-email`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notificationPreferences'] });
    },
  });
};

// Custom hook to unsubscribe from SMS notifications
export const useUnsubscribeSMS = () => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const queryClient = useQueryClient();

  const getToken = async (): Promise<string> => {
    try {
      return await getAccessTokenSilently({
        authorizationParams: {
          audience: "https://odp.golf/audience",
          scope: "openid profile email offline_access",
        },
      });
    } catch (error: any) {
      if (String(error?.message || "").includes('Missing Refresh Token')) {
        await loginWithRedirect({
          authorizationParams: {
            audience: "https://odp.golf/audience",
            scope: "openid profile email offline_access",
            prompt: 'login'
          }
        });
      }
      throw error;
    }
  };

  return useMutation({
    mutationFn: async (): Promise<NotificationResponse> => {
      const token = await getToken();

      const response = await axios.post<NotificationResponse>(`${endpointLink}user/notifications/unsubscribe-sms`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notificationPreferences'] });
    },
  });
};

// Types for notification groups
export interface NotificationGroupInfo {
  groupName: string;
  devices: string;
  description: string;
  restricted: number;
}

export interface NotificationGroupsResponse {
  success: boolean;
  groups: NotificationGroupInfo[];
}

export interface CurrentGroupResponse {
  success: boolean;
  groupNames: string[];
}

export interface UpdateGroupResponse {
  success: boolean;
  message: string;
  groupName: string | null;
}

// Custom hook to get available notification groups
export const useNotificationGroups = () => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const getToken = async (): Promise<string> => {
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: "https://odp.golf/audience",
          scope: "openid profile email offline_access",
        },
      });
      return token;
    } catch (error: any) {
      console.error('Token error:', error);
      if (String(error?.message || "").includes('Missing Refresh Token')) {
        console.log('Missing refresh token, redirecting to login...');
        await loginWithRedirect({
          authorizationParams: {
            audience: "https://odp.golf/audience",
            scope: "openid profile email offline_access",
            prompt: 'login'
          }
        });
      }
      throw error;
    }
  };

  return useQuery({
    queryKey: ['notificationGroups'],
    queryFn: async (): Promise<NotificationGroupInfo[]> => {
      const token = await getToken();

      const response = await axios.get<NotificationGroupsResponse>(`${endpointLink}user/notifications/groups`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data.groups;
    },
    staleTime: 1000 * 60 * 5, // Data is fresh for 5 minutes
  });
};

// Custom hook to get user's current notification groups
export const useCurrentNotificationGroups = (channel: 'email' | 'sms') => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const getToken = async (): Promise<string> => {
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: "https://odp.golf/audience",
          scope: "openid profile email offline_access",
        },
      });
      return token;
    } catch (error: any) {
      console.error('Token error:', error);
      if (String(error?.message || "").includes('Missing Refresh Token')) {
        console.log('Missing refresh token, redirecting to login...');
        await loginWithRedirect({
          authorizationParams: {
            audience: "https://odp.golf/audience",
            scope: "openid profile email offline_access",
            prompt: 'login'
          }
        });
      }
      throw error;
    }
  };

  return useQuery({
    queryKey: ['currentNotificationGroups', channel],
    queryFn: async (): Promise<string[]> => {
      const token = await getToken();

      const response = await axios.get<CurrentGroupResponse>(`${endpointLink}user/notifications/current-group?channel=${channel}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data.groupNames || [];
    },
    staleTime: 1000 * 60 * 5, // Data is fresh for 5 minutes
  });
};

// Custom hook to add a notification group
export const useAddNotificationGroup = () => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const queryClient = useQueryClient();

  const getToken = async (): Promise<string> => {
    try {
      return await getAccessTokenSilently({
        authorizationParams: {
          audience: "https://odp.golf/audience",
          scope: "openid profile email offline_access",
        },
      });
    } catch (error: any) {
      if (String(error?.message || "").includes('Missing Refresh Token')) {
        await loginWithRedirect({
          authorizationParams: {
            audience: "https://odp.golf/audience",
            scope: "openid profile email offline_access",
            prompt: 'login'
          }
        });
      }
      throw error;
    }
  };

  return useMutation({
    mutationFn: async ({ channel, groupName }: { channel: 'email' | 'sms'; groupName: string }): Promise<UpdateGroupResponse> => {
      const token = await getToken();

      const response = await axios.post<UpdateGroupResponse>(`${endpointLink}user/notifications/group`, 
        { channel, groupName },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      return response.data;
    },
    onSuccess: (_, variables) => {
      // Invalidate current groups query for the channel
      queryClient.invalidateQueries({ queryKey: ['currentNotificationGroups', variables.channel] });
    },
  });
};

// Custom hook to remove a notification group
export const useRemoveNotificationGroup = () => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const queryClient = useQueryClient();

  const getToken = async (): Promise<string> => {
    try {
      return await getAccessTokenSilently({
        authorizationParams: {
          audience: "https://odp.golf/audience",
          scope: "openid profile email offline_access",
        },
      });
    } catch (error: any) {
      if (String(error?.message || "").includes('Missing Refresh Token')) {
        await loginWithRedirect({
          authorizationParams: {
            audience: "https://odp.golf/audience",
            scope: "openid profile email offline_access",
            prompt: 'login'
          }
        });
      }
      throw error;
    }
  };

  return useMutation({
    mutationFn: async ({ channel, groupName }: { channel: 'email' | 'sms'; groupName: string }): Promise<UpdateGroupResponse> => {
      const token = await getToken();

      const response = await axios.delete<UpdateGroupResponse>(`${endpointLink}user/notifications/group?channel=${channel}&groupName=${encodeURIComponent(groupName)}`, 
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      return response.data;
    },
    onSuccess: (_, variables) => {
      // Invalidate current groups query for the channel
      queryClient.invalidateQueries({ queryKey: ['currentNotificationGroups', variables.channel] });
    },
  });
};
